/**
 * use-effective-selection
 *
 * Computes derived propagation/phase/task selections from context state
 * and reference data. Mirrors the useMemo logic previously inline in crop-modal.jsx.
 */

import { useMemo } from 'react'
import _ from 'lodash'
import { matchSorter } from 'match-sorter'
import { useCropModalContext } from '../context/crop-modal-context'
import { useCropTemplatePropagation } from './use-crop-template-propagation'
import { useCropPhaseTypesForPropagations } from './use-crop-phases-for-propagations'
import { doyToJSDate } from '~/common/utils/doy-utils'
import { PHASE_ORDER } from '../generators/generate-preview-schedule-data'
import { CROP_TASK_TYPES } from '../constants/crop-modal-constants'

export const useEffectiveSelection = ({ cropPropagationYypes, cropArchetypes, cropTemplates, climateData }) => {
  const {
    species,
    variety,
    selectedPropagationCode,
    selectedPhaseCodes,
    selectedTaskCodes,
    lifecycleType,
    isCustomCrop,
  } = useCropModalContext()

  const frostDates = useMemo(() => ({
    lfd: doyToJSDate(climateData?.avg_lfd),
    ffd: doyToJSDate(climateData?.avg_ffd),
  }), [climateData])

  // Fetch propagation codes from M2M junction table (skip for custom crops — no template)
  const { propagationCodes } = useCropTemplatePropagation({ templateId: isCustomCrop ? null : species?.id })

  // Effective propagation codes: M2M or archetype defaults; empty for custom crops (user selects manually)
  const effectivePropagationCodes = useMemo(() => {
    if (isCustomCrop) return []
    if (propagationCodes.length > 0) return propagationCodes

    const isDirectSowArchetype = ['direct_sow', 'fall_finisher', 'quick_start'].includes(species?.archetype_code)
    return [isDirectSowArchetype ? 'outdoor_sow' : 'indoor_sow']
  }, [isCustomCrop, propagationCodes, species?.archetype_code])

  const activePropagationCode = selectedPropagationCode ?? effectivePropagationCodes[0] ?? null

  const activePhaseGenerationCodes = useMemo(
    () => activePropagationCode ? [activePropagationCode] : effectivePropagationCodes,
    [activePropagationCode, effectivePropagationCodes]
  )

  const { cropPhaseTypes } = useCropPhaseTypesForPropagations({ propagationCodes: activePhaseGenerationCodes })

  // Available propagation type objects — for custom crops show all types
  const availableCropPropagationTypes = useMemo(() => {
    if (!cropPropagationYypes) return []
    if (isCustomCrop) return cropPropagationYypes
    return effectivePropagationCodes
      .map(code => cropPropagationYypes.find(pt => pt.code === code))
      .filter(Boolean)
  }, [isCustomCrop, cropPropagationYypes, effectivePropagationCodes])

  const isPerennial = lifecycleType === 'perennial'

  // Effective phase codes: user selection or defaults per propagation and lifecycle
  // Custom crops have no defaults — user must manually toggle phases
  const effectivePhaseCodes = useMemo(() => {
    if (selectedPhaseCodes !== null) return selectedPhaseCodes
    if (isCustomCrop) return []
    if (!cropPhaseTypes?.length) return null
    const available = cropPhaseTypes.map(pt => pt.code)
    let defaults
    if (isPerennial) {
      // Perennial defaults always include dormant, never decomposition
      if (activePropagationCode === 'cutting') {
        defaults = ['rooting', 'maturing', 'mature', 'dormant']
      } else if (activePropagationCode === 'transplant') {
        defaults = ['maturing', 'mature', 'dormant']
      } else if (activePropagationCode === 'outdoor_sow') {
        defaults = ['germination', 'maturing', 'mature', 'dormant']
      } else {
        defaults = ['germination', 'seedling', 'maturing', 'mature', 'dormant']
      }
    } else {
      if (activePropagationCode === 'cutting') {
        defaults = ['rooting', 'maturing', 'mature']
      } else if (activePropagationCode === 'transplant') {
        defaults = ['maturing', 'mature']
      } else {
        defaults = ['germination', 'seedling', 'maturing', 'mature']
      }
    }
    return defaults.filter(d => available.includes(d))
  }, [selectedPhaseCodes, isCustomCrop, cropPhaseTypes, activePropagationCode, isPerennial])

  // Effective task codes: user selection or defaults per propagation and lifecycle
  // Custom crops have no defaults — user must manually toggle tasks
  const effectiveTaskCodes = useMemo(() => {
    if (selectedTaskCodes !== null) return selectedTaskCodes
    if (isCustomCrop) return []
    const isDirectSow = (activePropagationCode ?? effectivePropagationCodes[0]) === 'outdoor_sow'
    const availableTasks = CROP_TASK_TYPES.filter(task => {
      if (task.directSowOnly === true) return isDirectSow
      if (task.directSowOnly === false) return !isDirectSow
      return true
    }).map(t => t.code)
    let defaults
    if (isPerennial) {
      defaults = isDirectSow
        ? ['sow_outdoor', 'harvest', 'cut_back']
        : ['sow_indoor', 'transplant_outdoor', 'harvest', 'cut_back']
    } else {
      defaults = isDirectSow
        ? ['sow_outdoor', 'harvest']
        : ['sow_indoor', 'transplant_outdoor', 'harvest']
    }
    return defaults.filter(code => availableTasks.includes(code))
  }, [selectedTaskCodes, isCustomCrop, activePropagationCode, effectivePropagationCodes, isPerennial])

  // Effective template: variety overrides species for scheduling
  const effectiveTemplate = useMemo(() => variety ?? species, [variety, species])

  // Sort species-only templates (parent_template_id = null) by archetype label for grouped Autocomplete
  const sortedCropTemplates = useMemo(() => {
    if (!cropTemplates || !cropArchetypes) return []
    return _.sortBy(
      cropTemplates.filter(t => !t.parent_template_id),
      template => {
        const archetype = cropArchetypes.find(a => a.code === template.archetype_code)
        return archetype?.label || ''
      }
    )
  }, [cropTemplates, cropArchetypes])

  // Fuzzy search filter for family types (name + latin_name)
  const filterFamilyOptions = useMemo(() => (options, { inputValue }) =>
    matchSorter(options, inputValue, { keys: ['name', 'latin_name'] })
  , [])

  // Fuzzy search filter maintaining archetype grouping
  const filterSpeciesOptions = useMemo(() => (options, { inputValue }) => {
    const filtered = matchSorter(options, inputValue, { keys: ['name', 'latin_name'] })
    return _.sortBy(filtered, template => {
      const archetype = cropArchetypes.find(a => a.code === template.archetype_code)
      return archetype?.label || ''
    })
  }, [cropArchetypes])

  return {
    propagationCodes,
    effectivePropagationCodes,
    activePropagationCode,
    availableCropPropagationTypes,
    cropPhaseTypes,
    effectivePhaseCodes,
    effectiveTaskCodes,
    sortedCropTemplates,
    filterSpeciesOptions,
    filterFamilyOptions,
    frostDates,
    isPerennial,
    effectiveTemplate,
  }
}
